/**
 * eCare Design System Tokens
 *
 * 3-tier token architecture: Primitive → Alias → Component.
 * Values follow `docs/ecare-styleguide.md` (v1.1); the theme-aware CSS custom
 * properties live in `src/styles/ecare-design-system.css`.
 *
 * NEVER hardcode hex values in components — always import from here.
 */

// ═══════════════════════════════════════════════════════════════════════════════
// LAYER 1: PRIMITIVE TOKENS — Raw values (hex colors, spacing, typography)
// ═══════════════════════════════════════════════════════════════════════════════

export const PRIMITIVE = {
  color: {
    brandBlue: {
      25: "#F2F6FB",
      50: "#E4ECF5", // accent-soft
      100: "#C6D6E8", // accent-soft-border
      200: "#9DB8D6",
      300: "#7499C4",
      400: "#4B7BB1",
      500: "#2B5D97",
      600: "#1A5090",
      700: "#0E4486", // Brand primary — accent-primary
      800: "#0C386F", // accent-primary-hover
      900: "#0A2F5C", // accent-primary-pressed
      1000: "#082644",
    },
    lightBlue: {
      "00": "#F7FAFD",
      50: "#EAF1F9",
      100: "#D2E1F1",
      200: "#AAC6E3",
      300: "#82ABD4",
      400: "#5A90C6",
      500: "#3576B8",
      550: "#2B6AAD",
      600: "#1F5C9E",
      700: "#164A82",
      800: "#0E3760",
      900: "#08243E",
    },
    darkBlue: {
      50: "#F2F6FB",
      100: "#E7ECF8", // bg-sidebar
      200: "#D3DDEE",
      300: "#B8C7E0",
      400: "#8FA5C6",
      500: "#5E7CA6",
      600: "#2B4D76", // accent-soft-text
      700: "#1E3E64",
      800: "#163353",
      900: "#102842",
      1000: "#0A1D31",
    },
    neutrals: {
      "000": "#FFFFFF",
      100: "#FDFEFF",
      200: "#FFFFFF", // bg-input
      300: "#FAFBFD",
      400: "#F7F9FC", // bg-base
      500: "#F4F7FB",
      550: "#F2F6FB",
      600: "#EFF3FB", // bg-code
      700: "#ECF0F9", // bg-hover
      800: "#EAEEF6", // border-divider
      900: "#E7ECF5",
      1000: "#E2E7F1", // border-default
      1100: "#DFE6F5", // bg-selected
      1200: "#CCD5E4", // border-strong
      1300: "#C6D6E8",
      1400: "#BFC8D6",
      1500: "#B7C0CE",
      1600: "#AEB6C2", // text-disabled
      1700: "#9AA3B1",
      1800: "#8A93A4", // chart-axis
      1900: "#7C8595",
      2000: "#69727F", // text-tertiary
      2100: "#5A6472",
      2200: "#46505F", // text-secondary
      2300: "#333D4C",
      2400: "#1C2330", // text-primary — darkest ink
      2500: "#171D28",
      2600: "#12171F",
      2700: "#0E1421", // dark canvas
    },
    green: {
      "00": "#F0F7F3",
      50: "#E6F1EA", // success-bg
      100: "#D2E6DA",
      200: "#B0D4BD",
      300: "#8CC1A0",
      400: "#66A97F",
      500: "#3F8F5C", // success
      600: "#35784D",
      700: "#2B613E",
      800: "#204A2F",
      900: "#16321F",
    },
    red: {
      "00": "#FCF0EE",
      50: "#F7E0DC", // error-bg
      100: "#EFC4BD",
      200: "#E09E93",
      300: "#D0786A",
      400: "#C15748",
      500: "#B33A2C", // error
      600: "#983125",
      700: "#7C281E",
      800: "#5D1E17",
      900: "#3E140F",
    },
    orange: {
      "00": "#FDF7EC",
      50: "#FAEED9", // warning-bg
      100: "#F3DCB4",
      200: "#E9C286",
      300: "#DDA85C",
      400: "#D39240",
      500: "#C77A2C", // warning
      600: "#A96525",
      700: "#8A521E",
      800: "#683E17",
      900: "#45290F",
    },
    // Accent hues below are drawn from the sanctioned chart palette (§3.6) so
    // status badges stay inside the design system's hue set.
    teal: {
      "00": "#EFF5F5",
      50: "#DFEBEA",
      100: "#C8DDDC",
      200: "#AECFCD",
      300: "#8DBBB8",
      400: "#75A5A2",
      500: "#5C8C8A", // chart-7
      600: "#4D7574",
      700: "#3D5E5D",
      800: "#2E4746",
      900: "#1E302F",
    },
    violet: {
      "00": "#F2EEF7",
      50: "#E5DDEF",
      100: "#CDBEDF",
      200: "#B49FCF",
      300: "#9C81BF",
      400: "#8A6DB3",
      500: "#7B5BA6", // chart-5
      600: "#674C8B",
      700: "#533D70",
      800: "#3F2E55",
      900: "#2A1F39",
    },
    purple: {
      "00": "#F4EFF8",
      50: "#E8DFF1",
      100: "#D6C6E6",
      200: "#C0A9D9",
      300: "#A98CCC",
      400: "#936FBF",
      500: "#7B5BA6", // chart-5
      600: "#674C8B",
      700: "#533D70",
      800: "#3F2E55",
      900: "#8A6DB3",
    },
    pink: {
      "00": "#F9EDF2",
      50: "#F2DAE4",
      100: "#E5B5C9",
      200: "#D890AE",
      300: "#CB6B93",
      400: "#BF527F",
      500: "#B33A6B", // chart-8
      600: "#98315B",
      700: "#7C284A",
      800: "#5D1E38",
      900: "#3E1425",
    },
  },

  spacing: {
    0: 0,
    1: 1,
    1.5: 1.5,
    2: 2,
    4: 4,
    8: 8,
    12: 12,
    16: 16,
    20: 20,
    24: 24,
    28: 28,
    32: 32,
    36: 36,
    40: 40,
    44: 44,
    48: 48,
    52: 52,
    56: 56,
    60: 60,
    64: 64,
    80: 80,
    100: 100,
    120: 120,
  } as const,

  /**
   * eCare radius scale (§4). Numeric keys are retained for existing callers;
   * the values are the design system's 6–30px steps, not raw pixels.
   */
  radius: {
    0: 0,
    2: 6, // xs
    4: 9, // sm
    8: 12, // md
    12: 14, // control — buttons, inputs, dropdowns
    16: 18, // lg — cards, KPI tiles
    20: 24, // xl — modals
    24: 30, // 2xl
    max: 9999,
  } as const,

  typography: {
    fontFamily:
      '"Inter", "Styrene B", "S\u00f6hne", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
    /** Serif is reserved for H1/H2 and page titles — the "moments" (§2.1). */
    fontFamilySerif:
      '"Source Serif 4", "Source Serif Pro", "Tiempos Text", "Charter", "Iowan Old Style", Georgia, serif',
    /** Mono carries every number a user might compare. */
    fontFamilyMono:
      '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, Consolas, monospace',
    fontWeight: {
      light: 300,
      regular: 400,
      medium: 500,
      semiBold: 600,
      bold: 700,
    },
    fontSize: {
      heading: { h1: 40, h2: 32, h3: 28, h4: 24 },
      title: { t1: 24, t2: 20, t3: 18, t4: 16, t5: 14, t6: 12 },
      body: { b1: 20, b2: 18, b3: 16, b4: 14, b5: 12, b6: 10 },
    },
    lineHeight: {
      "120%": {
        10: 12,
        12: 14,
        14: 18,
        16: 20,
        18: 22,
        20: 24,
        24: 30,
        28: 34,
        32: 38,
        40: 48,
      },
      "150%": {
        10: 14,
        12: 18,
        14: 20,
        16: 24,
        18: 26,
        20: 30,
        24: 36,
        28: 42,
        32: 48,
        40: 60,
      },
    },
  },

  shadow: {
    offset: {
      0: 0,
      100: 1,
      200: 2,
      300: 4,
      400: 8,
      500: 12,
      600: 16,
      700: 20,
      800: 24,
    },
    color: {
      light: "rgba(30, 40, 60, 0.04)",
      medium: "rgba(30, 40, 60, 0.08)",
      high: "rgba(30, 40, 60, 0.18)",
    },
    /** Elevation ramp (§5). Resting cards carry no shadow — only a border. */
    elevation: {
      xs: "0 1px 2px rgb(30 40 60 / 0.04)",
      sm: "0 2px 6px rgb(30 40 60 / 0.06)",
      md: "0 4px 12px rgb(30 40 60 / 0.08), 0 2px 4px rgb(30 40 60 / 0.04)",
      lg: "0 12px 28px rgb(30 40 60 / 0.12), 0 4px 8px rgb(30 40 60 / 0.06)",
      xl: "0 24px 48px rgb(30 40 60 / 0.18), 0 8px 16px rgb(30 40 60 / 0.08)",
    },
  },

  overlay: {
    light: "rgba(30, 40, 60, 0.18)",
    medium: "rgba(30, 40, 60, 0.32)",
    dark: "rgba(30, 40, 60, 0.48)",
    smoke: "rgba(255, 255, 255, 0.4)",
  },
} as const;

// ═══════════════════════════════════════════════════════════════════════════════
// LAYER 2: ALIAS TOKENS — Semantic meanings referencing primitives
// ═══════════════════════════════════════════════════════════════════════════════

const c = PRIMITIVE.color;

export const ALIAS = {
  icon: {
    default: c.brandBlue[700],
    hover: c.brandBlue[800],
    pressed: c.brandBlue[900],
    white: c.neutrals[100],
    barelyGrey: c.neutrals[1200],
    lightGrey: c.neutrals[1800],
    midGrey: c.neutrals[2000],
    semiDarkGrey: c.neutrals[2200],
    darkGrey: c.neutrals[2400],
    black: c.neutrals[2600],
  },

  text: {
    default: c.brandBlue[700],
    hover: c.brandBlue[800],
    pressed: c.brandBlue[900],
    white: c.neutrals[100],
    barelyGrey: c.neutrals[1200],
    lightGrey: c.neutrals[1800],
    midGrey: c.neutrals[2000],
    semiDarkGrey: c.neutrals[2200],
    darkGrey: c.neutrals[2400],
    black: c.neutrals[2500],
  },

  semantics: {
    success: {
      bg: c.green[50],
      default: c.green[500],
      hover: c.green[600],
      pressed: c.green[700],
    },
    warning: {
      bg: c.orange[50],
      default: c.orange[500],
      hover: c.orange[600],
      pressed: c.orange[700],
    },
    error: {
      bg: c.red[50],
      default: c.red[500],
      hover: c.red[600],
      pressed: c.red[700],
    },
  },

  surface: {
    canvas: {
      blue: {
        z0: c.darkBlue[300],
        z1: c.darkBlue[200],
        z2: c.darkBlue[100],
        z3: c.darkBlue[50],
      },
      slate: {
        z0: c.neutrals[1000],
        z1: c.neutrals[700],
        z2: c.neutrals[400],
        z3: c.neutrals[200],
        z4: c.neutrals[100],
        z5: c.neutrals["000"],
      },
    },
    action: {
      primaryBg: {
        default: c.brandBlue[700],
        hover: c.brandBlue[800],
        pressed: c.brandBlue[900],
        focused: c.brandBlue[1000],
      },
      secondaryBg: {
        yellow: c.orange["00"],
        white: c.neutrals["000"],
        barelyGrey: c.neutrals[1200],
        lightGrey: c.neutrals[1800],
        midGrey: c.neutrals[2000],
        darkGrey: c.neutrals[2400],
        blue: c.brandBlue[25],
        hover: c.brandBlue[50],
        pressed: c.brandBlue[100],
      },
      input: {
        default: c.neutrals[200],
        // A focused field keeps its surface fill; the brand outline and focus
        // ring carry the state instead of a tinted background.
        selected: c.neutrals["000"],
        disabled: c.neutrals[1000],
      },
    },
  },

  outline: {
    default: c.brandBlue[700],
    white: c.neutrals["000"],
    lightBlue: c.brandBlue[50],
    lightBlueHover: c.brandBlue[500],
    lightBluePressed: c.brandBlue[700],
    hover: c.brandBlue[800],
    pressed: c.brandBlue[900],
    focused: c.brandBlue[1000],
    barelyGrey: c.neutrals[1200],
    lightGrey: c.neutrals[1800],
    midGrey: c.neutrals[2000],
    darkGrey: c.neutrals[2400],
    black: c.neutrals[2600],
    yellow: c.orange[500],
    red: c.red[500],
    width: PRIMITIVE.spacing[1],
  },

  overlay: {
    light: PRIMITIVE.overlay.light,
    medium: PRIMITIVE.overlay.medium,
    dark: PRIMITIVE.overlay.dark,
    smoke: PRIMITIVE.overlay.smoke,
  },
} as const;

// ═══════════════════════════════════════════════════════════════════════════════
// LAYER 3: COMPONENT TOKENS — Per-component design decisions
// ═══════════════════════════════════════════════════════════════════════════════

const s = PRIMITIVE.spacing;
const r = PRIMITIVE.radius;

export const COMPONENT = {
  button: {
    primary: {
      bg: {
        default: ALIAS.surface.action.primaryBg.default,
        hover: ALIAS.surface.action.primaryBg.hover,
        pressed: ALIAS.surface.action.primaryBg.pressed,
      },
      outline: { focused: ALIAS.outline.focused },
      icon: { default: ALIAS.icon.white },
      text: {
        default: ALIAS.text.white,
        hover: ALIAS.text.white,
        pressed: ALIAS.text.white,
        disabled: ALIAS.text.lightGrey,
      },
      padding: { x: s[8], y: s[4], gap: s[4] },
      radius: { default: r[12], round: r.max },
    },
    secondary: {
      bg: {
        default: ALIAS.surface.action.secondaryBg.white,
        hover: ALIAS.surface.action.secondaryBg.hover,
        pressed: ALIAS.surface.action.secondaryBg.pressed,
      },
      outline: {
        default: ALIAS.outline.default,
        hover: ALIAS.outline.hover,
        pressed: ALIAS.outline.hover,
        focused: ALIAS.outline.focused,
      },
      icon: {
        default: ALIAS.icon.default,
        hover: ALIAS.icon.hover,
        pressed: ALIAS.icon.pressed,
      },
      text: {
        default: ALIAS.text.default,
        hover: ALIAS.text.hover,
        pressed: ALIAS.text.hover,
        disabled: ALIAS.text.lightGrey,
      },
      padding: { x: s[8], y: s[4], gap: s[4] },
      radius: { default: r[12], round: r.max },
    },
    tertiary: {
      icon: {
        default: ALIAS.icon.default,
        hover: ALIAS.icon.hover,
        pressed: ALIAS.icon.pressed,
      },
      text: {
        default: ALIAS.text.default,
        hover: ALIAS.text.hover,
        pressed: ALIAS.text.pressed,
        disabled: ALIAS.text.lightGrey,
      },
      padding: { x: s[0], y: s[0], gap: s[4] },
    },
    icon: {
      bg: {
        default: ALIAS.surface.action.secondaryBg.blue,
        hover: ALIAS.surface.action.secondaryBg.hover,
        pressed: ALIAS.surface.action.secondaryBg.pressed,
      },
      outline: {
        default: ALIAS.outline.lightBlue,
        hover: ALIAS.outline.lightBlueHover,
        pressed: ALIAS.outline.lightBluePressed,
      },
      icon: {
        default: ALIAS.icon.default,
        hover: ALIAS.icon.hover,
        pressed: ALIAS.icon.pressed,
        disabled: ALIAS.icon.lightGrey,
      },
      padding: { x: s[8], y: s[8] },
      radius: r[8],
    },
    navbar: {
      top: {
        bg: { hover: ALIAS.surface.action.secondaryBg.hover },
        outline: { selected: ALIAS.outline.white, width: s[2] },
        icon: { default: ALIAS.icon.white },
        padding: { x: s[4], bottom: s[4] },
        radius: r[4],
        gap: s[2],
      },
      tertiary: {
        outline: { selected: ALIAS.outline.pressed, width: s[2] },
        icon: {
          default: ALIAS.icon.darkGrey,
          hover: ALIAS.icon.hover,
          selected: ALIAS.icon.pressed,
        },
        padding: { x: s[4], bottom: s[8] },
        gap: s[2],
      },
    },
  },

  inputField: {
    bg: {
      default: ALIAS.surface.action.input.default,
      selected: ALIAS.surface.action.input.selected,
      disabled: ALIAS.surface.action.input.disabled,
    },
    text: {
      default: ALIAS.text.lightGrey,
      hover: ALIAS.text.lightGrey,
      disabled: ALIAS.text.lightGrey,
      label: ALIAS.text.darkGrey,
    },
    outline: {
      default: ALIAS.outline.barelyGrey,
      hover: ALIAS.outline.lightBlueHover,
      selected: ALIAS.outline.default,
      error: ALIAS.outline.red,
      focused: ALIAS.outline.focused,
      disabled: ALIAS.outline.lightGrey,
    },
    padding: { x: s[12], y: s[8], gap2: s[2], gap4: s[4], gap8: s[8] },
    radius: { default: r[12], round: r.max },
  },

  checkbox: {
    iconBg: {
      default: ALIAS.surface.action.secondaryBg.white,
      hover: ALIAS.surface.action.secondaryBg.hover,
      pressed: ALIAS.surface.action.secondaryBg.pressed,
    },
    text: { default: ALIAS.text.default, disabled: ALIAS.text.lightGrey },
    radius: r[2],
  },

  toggle: {
    iconBg: {
      default: ALIAS.surface.action.secondaryBg.white,
      hover: ALIAS.surface.action.secondaryBg.hover,
      active: ALIAS.surface.action.secondaryBg.pressed,
    },
    text: {
      default: ALIAS.text.darkGrey,
      active: ALIAS.text.darkGrey,
      disabled: ALIAS.text.lightGrey,
    },
    outline: {
      default: ALIAS.outline.midGrey,
      hover: ALIAS.outline.default,
      active: ALIAS.outline.pressed,
    },
    radius: s[12],
    padding: s[4],
  },

  badge: {
    text: {
      default: ALIAS.text.white,
      neutral: ALIAS.text.white,
      positive: ALIAS.semantics.success.pressed,
      warning: ALIAS.semantics.warning.hover,
      negative: ALIAS.semantics.error.hover,
      appointmentStatus: {
        scheduled: c.orange[700],
        confirmed: c.brandBlue[800],
        instant: c.red[700],
        checkedIn: c.violet[600],
        inExam: c.teal[800],
        signedOff: c.green[800],
        noShow: c.neutrals[2400],
        cancelled: c.neutrals[2400],
      },
      appointmentType: {
        telehealth: c.pink[600],
        inPerson: c.neutrals[2400],
      },
    },
    bg: {
      default: ALIAS.surface.action.primaryBg.default,
      neutral: ALIAS.surface.action.secondaryBg.midGrey,
      positive: ALIAS.semantics.success.bg,
      warning: ALIAS.semantics.warning.bg,
      negative: ALIAS.semantics.error.bg,
      appointmentStatus: {
        scheduled: c.orange[100],
        confirmed: c.brandBlue[100],
        instant: c.red[100],
        checkedIn: c.violet[50],
        inExam: c.teal[50],
        signedOff: c.green[100],
        noShow: c.neutrals[1000],
        cancelled: c.neutrals[1700],
      },
      appointmentType: {
        telehealth: c.pink[50],
        inPerson: c.neutrals[1000],
      },
    },
    icon: {
      default: ALIAS.text.white,
      neutral: ALIAS.text.white,
      positive: ALIAS.semantics.success.pressed,
      warning: ALIAS.semantics.warning.hover,
      negative: ALIAS.semantics.error.hover,
    },
    radius: r.max,
    padding: { x: s[4], y: s[0] },
    gap: s[2],
  },

  tag: {
    text: {
      default: ALIAS.text.default,
      hover: ALIAS.text.hover,
      active: ALIAS.icon.white,
    },
    bg: {
      default: ALIAS.surface.action.secondaryBg.white,
      hover: ALIAS.surface.action.secondaryBg.pressed,
      active: ALIAS.surface.action.primaryBg.default,
    },
    outline: {
      default: ALIAS.outline.default,
      hover: ALIAS.outline.hover,
      filter: ALIAS.icon.pressed,
    },
    icon: {
      default: ALIAS.icon.default,
      hover: ALIAS.icon.hover,
      active: ALIAS.icon.white,
    },
    radius: r.max,
    padding: { x: s[8], y: s[4] },
    gap: s[4],
  },

  avatar: {
    text: ALIAS.text.darkGrey,
    bg: ALIAS.surface.canvas.slate.z0,
    radius: r.max,
    statusLive: ALIAS.semantics.success.default,
  },

  scrollbar: {
    default: ALIAS.surface.action.secondaryBg.barelyGrey,
    bg: ALIAS.surface.canvas.slate.z2,
    hover: ALIAS.surface.action.secondaryBg.lightGrey,
    pressed: ALIAS.surface.action.secondaryBg.midGrey,
    width: s[24],
    height: s[24],
  },

  table: {
    bg: {
      header: c.neutrals[400],
      row1: ALIAS.surface.action.secondaryBg.white,
      row2: c.neutrals[400],
      lastRow1: ALIAS.surface.action.secondaryBg.white,
      lastRow2: c.neutrals[300],
      footer: ALIAS.surface.action.secondaryBg.white,
    },
    rowHeight: s[28],
    radius: r[8],
    outline: ALIAS.outline.barelyGrey,
    gap: s[4],
    padding: s[12],
  },

  navbar: {
    top: {
      bg: c.brandBlue[700],
      height: s[44],
      outline: ALIAS.outline.barelyGrey,
      gap: { 16: s[16], 20: s[20] },
    },
  },

  tab: {
    pinboard: {
      bg: {
        default: ALIAS.surface.action.secondaryBg.blue,
        hover: ALIAS.semantics.warning.bg,
        selected: ALIAS.semantics.warning.bg,
      },
      outline: {
        default: ALIAS.outline.lightGrey,
        hover: ALIAS.outline.default,
        selected: ALIAS.outline.default,
      },
      iconHoverSelected: ALIAS.icon.pressed,
      padding: { x: s[8], y: s[4] },
      radius: r[8],
      gap: s[4],
    },
  },

  card: {
    radius: r[16],
    bg: ALIAS.surface.canvas.slate.z5,
  },

  modal: {
    radius: r[20],
  },

  separator: {
    color: ALIAS.outline.barelyGrey,
  },
} as const;

// ═══════════════════════════════════════════════════════════════════════════════
// SCREEN TOKENS — Login page (LoginV2)
// ═══════════════════════════════════════════════════════════════════════════════

export const LOGIN_TOKENS = {
  color: {
    /** Brand primary — headings, links, button bg, checkbox checked */
    primary: PRIMITIVE.color.brandBlue[700], // #0E4486
    /** Primary button hover bg */
    primaryHover: PRIMITIVE.color.brandBlue[800], // #0C386F
    /** Welcome subheading text */
    subheadingText: PRIMITIVE.color.neutrals[2200], // #46505F
    /** Input adornment icon (mail) */
    iconGrey: PRIMITIVE.color.neutrals[1600], // #AEB6C2
    /** "Remember me" label text */
    rememberMeText: PRIMITIVE.color.neutrals[2200], // #46505F
    /** Unchecked checkbox outline */
    checkboxUnchecked: PRIMITIVE.color.neutrals[1700], // #9AA3B1
    /** 2FA note text */
    noteText: PRIMITIVE.color.neutrals[2000], // #69727F
  },
  fontFamily: PRIMITIVE.typography.fontFamily,
  fontWeight: PRIMITIVE.typography.fontWeight,
  fontSize: {
    /** Welcome heading */
    heading: "22px",
    /** Body copy (subheading, labels, note) */
    body: "14px",
    /** Primary button text */
    button: "16px",
    /** Checkbox icon */
    checkboxIcon: 20,
    /** Small inline icon (e.g. mail adornment) */
    iconSm: 16,
  },
  height: "52px",
} as const;

// ═══════════════════════════════════════════════════════════════════════════════
// SCREEN TOKENS — Site unreachable fallback (login group API failure)
// ═══════════════════════════════════════════════════════════════════════════════

export const SITE_UNREACHABLE_TOKENS = {
  color: {
    /** Near-black heading */
    heading: PRIMITIVE.color.neutrals[2400], // #1C2330
    /** Body copy + helper text */
    body: PRIMITIVE.color.neutrals[2200], // #46505F
    /** Error code (smaller, muted) */
    errorCode: PRIMITIVE.color.neutrals[2000], // #69727F
    /** Broken-page icon stroke/fill */
    icon: PRIMITIVE.color.neutrals[1800], // #8A93A4
    /** Link + primary button bg */
    primary: PRIMITIVE.color.brandBlue[700], // #0E4486
    /** Primary button hover bg */
    primaryHover: PRIMITIVE.color.brandBlue[800], // #0C386F
    /** Button label */
    buttonText: PRIMITIVE.color.neutrals["000"], // #FFFFFF
    /** Page background */
    background: PRIMITIVE.color.neutrals["000"], // #FFFFFF
  },
  fontFamily: PRIMITIVE.typography.fontFamily,
  fontWeight: PRIMITIVE.typography.fontWeight,
  fontSize: {
    heading: `${PRIMITIVE.typography.fontSize.heading.h3}px`, // 28px
    body: `${PRIMITIVE.typography.fontSize.body.b3}px`, // 16px
    errorCode: `${PRIMITIVE.typography.fontSize.body.b5}px`, // 12px
    button: `${PRIMITIVE.typography.fontSize.body.b4}px`, // 14px
  },
} as const;

// ═══════════════════════════════════════════════════════════════════════════════
// CONVENIENCE HELPERS
// ═══════════════════════════════════════════════════════════════════════════════

/** Pixel string helper: `px(8)` → `"8px"` */
export const px = (v: number) => `${v}px`;
